// src/pages/AuthFotoOnBoarding.js
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Camera, LogOut, ShieldCheck, RotateCcw, Save } from "lucide-react";

import ProgressRing from "../components/ui/ProgressRing";
import LoadingSpinner from "../components/LoadingSpinner";
import authService from "../services/authService";
import { useAuth } from "../context/AuthContext";

/* =========================
   CSS embebido (1 vez)
========================= */
const CSS = `
.af-wrap{
  min-height:100vh;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:32px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef4ff 100%);
}
.af-wrap.af-wrap--embedded{
  position:fixed;
  inset:0;
  min-height:auto;
  padding:0;
  background:transparent;
  z-index:9998;
  pointer-events:none;
}
.af-card{ width:min(980px, 100%); background: rgba(255,255,255,.92); border:1px solid rgba(15,23,42,.10); border-radius:18px; box-shadow: 0 18px 60px rgba(23, 58, 8, 0.8); overflow:hidden; }
.af-grid{ display:grid; grid-template-columns: 1.1fr .9fr; gap:0; }
.af-left{ padding:26px 26px 24px; }
.af-right{ padding:26px; background: linear-gradient(180deg, rgba(15,95,184,.08), rgba(99,102,241,.06)); border-left:1px solid rgba(15,23,42,.08); }
.af-title{ font-size: clamp(1.35rem, 2.4vw, 2.05rem); font-weight:900; letter-spacing:-.02em; margin:0 0 10px; color: #0f172a; display:flex; align-items:center; gap:10px; }
.af-sub{ margin:0 0 18px; color: #334155; font-weight:600; line-height:1.4; }
.af-bullets{ margin:0; padding:0; list-style:none; display:grid; gap:10px; }
.af-bullets li{ display:flex; gap:10px; align-items:flex-start; color: #0f172a; font-weight:700; }
.af-dot{ width:10px; height:10px; border-radius:999px; margin-top:6px; background: #0f5fb8; box-shadow: 0 0 0 4px rgba(15,95,184,.15); }
.af-note{ margin-top:14px; padding:12px 12px; border-radius:14px; background: rgba(245,158,11,.10); border:1px solid rgba(245,158,11,.25); color:#78350f; font-weight:800; display:flex; gap:10px; align-items:flex-start; }

.af-camBox{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; }
.af-ringWrap{ display:flex; align-items:center; justify-content:center; }
.af-videoShell{ width: 200px; height: 200px; border-radius:999px; overflow:hidden; background: #2c3036bb; box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.18); }
.af-video{ width:100%; height:100%; object-fit:cover; transform: scaleX(-1); }
.af-status{ width:100%; text-align:center; font-weight:900; color: #0f172a; }
.af-status small{ display:block; margin-top:6px; color: #64748b; font-weight:800; }

.af-actions{ display:flex; gap:10px; justify-content:center; flex-wrap:wrap; }
.af-btn{ border:0; border-radius:12px; padding:10px 14px; font-weight:900; cursor:pointer; display:inline-flex; align-items:center; gap:8px; }
.af-btnPrimary{ background: #0f5fb8; color:#fff; box-shadow: 0 10px 24px rgba(15,95,184,.28); }
.af-btnGhost{ background: rgba(15,23,42,.06); color:#0f172a; }
.af-btnDanger{ background: rgba(220,38,38,.10); color:#991b1b; }
.af-btnDanger:hover{ background: rgba(220,38,38,.16); }
.af-btn:disabled{ opacity:.55; cursor:not-allowed; }

.af-modalOverlay{ position:fixed; inset:0; background: rgba(15,23,42,.56); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display:flex; align-items:center; justify-content:center; padding:18px; z-index:9999; }
.af-wrap.af-wrap--embedded .af-modalOverlay{ pointer-events:auto; }
.af-modal{ width:min(560px, 100%); background: rgba(255,255,255,.98); border-radius:18px; box-shadow: 0 22px 60px rgba(0,0,0,.22); border:1px solid rgba(0,0,0,.06); overflow:hidden; }
.af-modalHead{ padding:16px 16px 10px; border-bottom:1px solid rgba(15,23,42,.08); background:#fff; }
.af-modalTitle{ margin:0; font-weight:950; font-size:1.05rem; color: #0f172a; }
.af-modalBody{ padding:14px 16px 6px; background:#fff; }
.af-preview{ width:100%; border-radius:14px; background: #0b1220; aspect-ratio: 4/3; overflow:hidden; display:flex; align-items:center; justify-content:center; }
.af-preview img{ width:100%; height:100%; object-fit:cover; }
.af-modalFoot{ padding:14px 16px 16px; display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap; background:#fff; }
.af-checkRow{
  margin-top:14px;
  display:flex;
  gap:10px;
  align-items:flex-start;
  font-weight:900;
  color:#0f172a;
  background: rgba(15,23,42,.04);
  border:1px solid rgba(15,23,42,.10);
  border-radius:12px;
  padding:12px;
}
.af-checkRow input{
  margin-top:3px;
  width:18px;
  height:18px;
  accent-color:#0f5fb8;
  cursor:pointer;
}
.af-modalWide{ width:min(720px, 100%); }
.af-modalCam{ width:min(520px, 100%); }
.af-guide{
  position:absolute;
  inset:0;
  border-radius:999px;
  pointer-events:none;
  display:flex;
  align-items:center;
  justify-content:center;
}
.af-guide::before{
  content:"";
  width:78%;
  height:78%;
  border-radius:999px;
  border:2px dashed rgba(255,255,255,.55);
  box-shadow: 0 0 0 999px rgba(0,0,0,.12) inset;
}
.af-guideLabel{
  position:absolute;
  bottom:10px;
  left:50%;
  transform:translateX(-50%);
  padding:6px 10px;
  border-radius:999px;
  font-weight:900;
  font-size:.78rem;
  color:#fff;
  background: rgba(15,23,42,.55);
  backdrop-filter: blur(6px);
  border:1px solid rgba(255,255,255,.15);
}
.af-guideBadge{
  position:absolute;
  top:10px;
  right:10px;
  padding:6px 10px;
  border-radius:999px;
  font-weight:950;
  font-size:.78rem;
  color:#fff;
  background: rgba(2,132,199,.75);
  border:1px solid rgba(255,255,255,.18);
}
.af-guideBadge.ok{ background: rgba(34,197,94,.78); }
.af-guideBadge.warn{ background: rgba(245,158,11,.78); }

@media (max-width: 860px){
  .af-grid{ grid-template-columns: 1fr; }
  .af-right{ border-left:0; border-top:1px solid rgba(15,23,42,.08); }
}
`;

function injectOnce() {
  if (typeof document === "undefined") return;
  if (document.getElementById("af-onboarding-css")) return;
  const s = document.createElement("style");
  s.id = "af-onboarding-css";
  s.textContent = CSS;
  document.head.appendChild(s);
}

const computeQuality = (videoEl) => {
  const sw = 160;
  const sh = 160;

  const c = document.createElement("canvas");
  c.width = sw;
  c.height = sh;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) return { bright: 999, sharp: 999, okLight: true, okSharp: true };

  ctx.drawImage(videoEl, 0, 0, sw, sh);
  const { data } = ctx.getImageData(0, 0, sw, sh);

  let sumL = 0;
  let gradSum = 0;

  for (let y = 1; y < sh - 1; y += 1) {
    for (let x = 1; x < sw - 1; x += 1) {
      const i = (y * sw + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      sumL += L;

      const ix = (y * sw + (x + 1)) * 4;
      const iy = ((y + 1) * sw + x) * 4;

      const rX = data[ix], gX = data[ix + 1], bX = data[ix + 2];
      const rY = data[iy], gY = data[iy + 1], bY = data[iy + 2];

      const LX = 0.2126 * rX + 0.7152 * gX + 0.0722 * bX;
      const LY = 0.2126 * rY + 0.7152 * gY + 0.0722 * bY;

      gradSum += Math.abs(L - LX) + Math.abs(L - LY);
    }
  }

  const pixels = (sw - 2) * (sh - 2);
  const bright = sumL / pixels;
  const sharp = gradSum / pixels;

  const okLight = bright >= 55;
  const okSharp = sharp >= 12;

  return { bright, sharp, okLight, okSharp };
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const hasFaceDetector = () => typeof window !== "undefined" && "FaceDetector" in window;

export default function AuthFotoOnboarding({ embedded = false, onPhotoSaved }) {
  useEffect(injectOnce, []);

  useEffect(() => {
    if (!embedded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [embedded]);

  const { refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const captureRafRef = useRef(null);
  const stableMsRef = useRef(0);
  const lastTickRef = useRef(0);
  const capturedOnceRef = useRef(false);
  const isCapturingRef = useRef(false);

  const [videoInputs, setVideoInputs] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState("");
  const [step, setStep] = useState("instructions");
  const [cameraFacing, setCameraFacing] = useState("user");
  const [camState, setCamState] = useState("requesting");
  const [progress, setProgress] = useState(0);
  const [faceOk, setFaceOk] = useState(false);
  const [supportFD] = useState(hasFaceDetector());
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fromTarget = useMemo(() => {
    const from = location?.state?.from;
    if (!from) return { pathname: "/" };
    if (String(from.pathname || "").startsWith("/login")) return { pathname: "/" };
    return from;
  }, [location?.state]);

  const loadVideoInputs = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      setVideoInputs(cams);
      return cams;
    } catch (e) {
      console.error("No se pudieron listar las cámaras:", e);
      return [];
    }
  };
  const startCamera = async (facing = cameraFacing, preferredDeviceId = "") => {
    setError("");
    setCamState("requesting");

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    let stream = null;

    try {
      // Si ya tenemos un deviceId concreto, úsalo
      if (preferredDeviceId) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: preferredDeviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } else {
        // Primer intento: facingMode (útil sobre todo en móviles)
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      }
    } catch (err) {
      console.warn("Fallo con constraints iniciales, probando modo básico:", err);

      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
    }

    streamRef.current = stream;

    const track = stream.getVideoTracks?.()[0];
    const settings = track?.getSettings?.() || {};
    const detectedDeviceId = settings.deviceId || "";

    if (detectedDeviceId) {
      setActiveDeviceId(detectedDeviceId);
    } setCamState("ready");

    const cams = await loadVideoInputs();

    if (!preferredDeviceId && !detectedDeviceId && cams.length > 0) {
      setActiveDeviceId(cams[0].deviceId);
    }
  };

  useEffect(() => {
    if (step !== "camera") return;

    let alive = true;

    const start = async () => {
      try {
        await startCamera(cameraFacing, activeDeviceId);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setCamState("denied");
        setError("No se pudo acceder a la cámara. Activa permisos para continuar.");
      }
    };

    start();

    return () => {
      alive = false;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;

      if (captureRafRef.current) cancelAnimationFrame(captureRafRef.current);
      captureRafRef.current = null;

      stableMsRef.current = 0;
      lastTickRef.current = 0;
      capturedOnceRef.current = false;
      isCapturingRef.current = false;

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [step]);

  useEffect(() => {
    if (camState !== "ready") return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;

    try {
      if (v.srcObject !== s) v.srcObject = s;
      v.muted = true;
      v.setAttribute("playsinline", "true");
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => { });
    } catch (e) {
      console.warn("No se pudo adjuntar stream al video:", e);
    }
  }, [camState]);

  // ===== detección + validación previa =====
  useEffect(() => {
    if (camState !== "ready") return;
    const v = videoRef.current;
    if (!v) return;

    let detector = null;

    if (supportFD) {
      try {
        detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
      } catch {
        detector = null;
      }
    }

    const tick = async () => {
      if (showPreview || isCapturingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      let faceDetectedOk = false;
      let nextError = "";

      try {
        if (v.readyState < 2) {
          nextError = "La cámara aún no está lista.";
        } else if (detector) {
          const faces = await detector.detect(v);

          if (!Array.isArray(faces) || faces.length === 0) {
            nextError = "Coloca tu rostro centrado dentro del círculo.";
          } else if (faces.length > 1) {
            nextError = "Debe aparecer solo un rostro en cámara.";
          } else {
            const box = faces[0].boundingBox;
            const vw = v.videoWidth || 1;
            const vh = v.videoHeight || 1;

            const cx = box.x + box.width / 2;
            const cy = box.y + box.height / 2;

            const centerOk =
              Math.abs(cx - vw / 2) <= vw * 0.18 &&
              Math.abs(cy - vh / 2) <= vh * 0.22;

            const sizeOk = box.width >= vw * 0.28 && box.width <= vw * 0.78;

            if (!centerOk || !sizeOk) {
              nextError = "Ajusta tu rostro para que quede centrado y visible.";
            } else {
              faceDetectedOk = true;
            }
          }
        } else {
          faceDetectedOk = v.readyState >= 2;
        }

        let quality = { okLight: true, okSharp: true };
        try {
          if (v.readyState >= 2) quality = computeQuality(v);
        } catch { }

        if (!nextError && !quality.okLight) {
          nextError = "Está muy oscuro. Busca más luz.";
        } else if (!nextError && !quality.okSharp) {
          nextError = "La imagen está borrosa. Mantente quieto.";
        }

        const okFinal = faceDetectedOk && quality.okLight && quality.okSharp;

        setFaceOk(okFinal);
        setError(nextError);
        setProgress(0);
      } catch {
        setFaceOk(false);
        setError("No se pudo analizar la cámara. Intenta nuevamente.");
        setProgress(0);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [camState, supportFD, showPreview]);

  useEffect(() => {
    return () => {
      if (captureRafRef.current) cancelAnimationFrame(captureRafRef.current);
      captureRafRef.current = null;
    };
  }, []);

  const captureNow = async () => {
    try {
      const v = videoRef.current;
      if (!v || v.readyState < 2) throw new Error("Video no listo");

      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 720;

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No se pudo crear canvas");

      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(v, 0, 0, w, h);

      const b = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!b) throw new Error("No se pudo generar imagen");

      const url = URL.createObjectURL(b);
      setBlob(b);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (e) {
      console.error(e);
      setError("No se pudo tomar la foto. Intenta nuevamente.");
      capturedOnceRef.current = false;
      stableMsRef.current = 0;
      setProgress(0);
    }
  };

  const onRetry = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setShowPreview(false);
    setPreviewUrl("");
    setBlob(null);
    setError("");
    stableMsRef.current = 0;
    lastTickRef.current = 0;
    capturedOnceRef.current = false;
    isCapturingRef.current = false;
    setProgress(0);
  };

  const handleLogout = () => {
    if (captureRafRef.current) cancelAnimationFrame(captureRafRef.current);
    captureRafRef.current = null;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setShowPreview(false);
    setPreviewUrl("");
    setBlob(null);
    setSaving(false);
    logout();
    navigate("/login", { replace: true });
  };

  const onSave = async () => {
    if (!blob) return;
    setSaving(true);
    setError("");
    try {
      await authService.uploadAuthPhoto(blob, "auth.jpg");
      await refreshUser();
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setShowPreview(false);

      if (embedded && onPhotoSaved) {
        await onPhotoSaved();
      } else {
        navigate(fromTarget || "/", { replace: true });
      }
    } catch (e) {
      console.error(e);
      setError(e?.payload?.error || e?.message || "No se pudo guardar la foto.");
      setSaving(false);
    }
  };

  const captureButtonDisabled =
    camState !== "ready" ||
    showPreview ||
    saving ||
    isCapturingRef.current ||
    !!error ||
    !faceOk;

  const manualStart = async () => {
    if (camState !== "ready" || error || !faceOk || showPreview || isCapturingRef.current) return;

    isCapturingRef.current = true;
    setProgress(0);

    const start = performance.now();
    const duration = 1000;

    const run = async (t) => {
      const p = clamp(((t - start) / duration) * 100, 0, 100);
      setProgress(p);

      if (p >= 100) {
        try {
          await captureNow();
        } finally {
          isCapturingRef.current = false;
          setProgress(0);
        }
        return;
      }

      captureRafRef.current = requestAnimationFrame(run);
    };

    captureRafRef.current = requestAnimationFrame(run);
  };

  const statusText = (() => {
    if (camState === "requesting") return "Solicitando permisos de cámara…";
    if (camState === "denied") return "Permisos de cámara requeridos";
    if (showPreview) return "Revisa tu foto";
    if (isCapturingRef.current) return "Tomando foto…";
    if (faceOk && !error) return "Todo listo para capturar";
    return "Ajusta la cámara para habilitar la captura";
  })();

  const statusSub = (() => {
    if (camState === "denied") return error || "";
    if (showPreview) return "Debes guardarla para poder entrar";
    return "Sin gorras, gorros, sombreros ni gafas oscuras";
  })();

  const goNext = () => {
    setStep("camera");
  };

  const goBack = () => {
    setStep("instructions");
    setCamState("requesting");
    setProgress(0);
    setFaceOk(false);
    setError("");
    stableMsRef.current = 0;
    lastTickRef.current = 0;
    capturedOnceRef.current = false;
    isCapturingRef.current = false;
  };
  const handleSwitchCamera = async () => {
    if (saving || showPreview || camState === "requesting") return;

    try {
      setError("");
      setProgress(0);
      setFaceOk(false);
      stableMsRef.current = 0;
      lastTickRef.current = 0;
      capturedOnceRef.current = false;
      isCapturingRef.current = false;

      let cams = videoInputs;

      if (!cams.length) {
        cams = await loadVideoInputs();
      }

      // Si hay 2 o más cámaras, alternar por deviceId real
      if (cams.length >= 2) {
        const currentIndex = cams.findIndex((c) => c.deviceId === activeDeviceId);
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % cams.length : 0;
        const nextCam = cams[nextIndex];

        setActiveDeviceId(nextCam.deviceId);
        await startCamera(cameraFacing, nextCam.deviceId);
        return;
      }

      // Fallback para móviles o navegadores que no exponen bien varias cámaras
      const nextFacing = cameraFacing === "user" ? "environment" : "user";
      setCameraFacing(nextFacing);
      await startCamera(nextFacing, "");
    } catch (e) {
      console.error(e);
      setError("No se pudo cambiar la cámara.");
    }
  };
  const handleSelectCamera = async (deviceId) => {
    if (!deviceId || saving || showPreview || camState === "requesting") return;

    try {
      setError("");
      setProgress(0);
      setFaceOk(false);
      stableMsRef.current = 0;
      lastTickRef.current = 0;
      capturedOnceRef.current = false;
      isCapturingRef.current = false;

      setActiveDeviceId(deviceId);
      await startCamera(cameraFacing, deviceId);
    } catch (e) {
      console.error(e);
      setError("No se pudo cambiar a la cámara seleccionada.");
    }
  };

  const isMobileDevice =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);


  const showCameraDropdown = !isMobileDevice && videoInputs.length > 1;
  const showSwitchCameraButton = isMobileDevice;
  return (
    <div className={`af-wrap ${embedded ? "af-wrap--embedded" : ""}`}>
      {step === "instructions" ? (
        <div className="af-modalOverlay" role="dialog" aria-modal="true">
          <div className="af-modal af-modalWide">
            <div className="af-modalHead">
              <h3 className="af-modalTitle" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ShieldCheck size={20} /> Identifícate antes de entrar
              </h3>
            </div>

            <div className="af-modalBody">
              <p className="af-sub" style={{ marginTop: 0 }}>
                Para continuar, necesitamos una foto clara de tu rostro. Esta foto se guardará en tu perfil y se pedirá
                solo si aún no tienes una.
              </p>

              <ul className="af-bullets">
                <li><span className="af-dot" /><span>Posiciónate frente a la cámara y muestra completamente tu rostro.</span></li>
                <li><span className="af-dot" /><span>No uses gorras, gorros, sombreros ni gafas oscuras.</span></li>
                <li><span className="af-dot" /><span>Cuando todo esté correcto, el botón de captura se habilitará.</span></li>
              </ul>
            </div>

            <div className="af-modalFoot" style={{ justifyContent: "space-between" }}>
              <button className="af-btn af-btnDanger" onClick={handleLogout} type="button">
                <LogOut size={18} /> Cerrar sesión
              </button>

              <button className="af-btn af-btnPrimary" onClick={goNext}>
                Siguiente
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {step === "camera" ? (
        <div className="af-modalOverlay" role="dialog" aria-modal="true">
          <div className="af-modal af-modalCam">
            <div className="af-modalHead">
              <h3 className="af-modalTitle" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Camera size={18} /> Toma tu foto
              </h3>
            </div>

            <div className="af-modalBody">
              <div className="af-camBox">
                {camState !== "ready" && camState !== "denied" ? (
                  <LoadingSpinner message="Preparando cámara…" />
                ) : (
                  <div className="af-ringWrap">
                    <ProgressRing value={progress} size={220} stroke={14} glow label="" showPercent={false} start="top" duration={100}>
                      <div className="af-videoShell" aria-label="Cámara">
                        <video ref={videoRef} className="af-video" muted autoPlay playsInline />
                      </div>
                    </ProgressRing>
                  </div>
                )}

                <div className="af-status">
                  {statusText}
                  {statusSub ? <small>{statusSub}</small> : null}
                </div>

                {camState === "denied" ? (
                  <div className="af-actions">
                    <button className="af-btn af-btnPrimary" onClick={() => window.location.reload()}>
                      <Camera size={18} /> Reintentar permisos
                    </button>
                  </div>
                ) : null}

                {camState === "ready" && !showPreview ? (
                  <div
                    className="af-actions"
                    style={{ flexDirection: "column", alignItems: "center", width: "100%" }}
                  >
                    <button
                      className="af-btn af-btnPrimary"
                      onClick={manualStart}
                      disabled={captureButtonDisabled}
                    >
                      <Camera size={18} /> Iniciar captura
                    </button>

                    {showCameraDropdown ? (
                      <div style={{ width: "100%", maxWidth: 320 }}>
                        <select
                          value={activeDeviceId || ""}
                          onChange={(e) => handleSelectCamera(e.target.value)}
                          disabled={saving || showPreview || camState === "requesting"}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: 12,
                            border: "1px solid rgba(15,23,42,.14)",
                            background: "#fff",
                            color: "#0f172a",
                            fontWeight: 800,
                            outline: "none",
                          }}
                        >
                          <option value="" disabled>
                            Selecciona una cámara
                          </option>
                          {videoInputs.map((cam, idx) => (
                            <option key={cam.deviceId || idx} value={cam.deviceId}>
                              {cam.label || `Cámara ${idx + 1}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {error ? (
                  <div style={{ width: "100%", color: "#b91c1c", fontWeight: 900, textAlign: "center" }}>
                    {error}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="af-modalFoot" style={{ justifyContent: "space-between" }}>
              <button className="af-btn af-btnDanger" onClick={handleLogout} disabled={saving} type="button">
                <LogOut size={18} /> Cerrar sesión
              </button>

              <button className="af-btn af-btnGhost" onClick={goBack} disabled={saving || showPreview}>
                Atrás
              </button>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {showSwitchCameraButton ? (
                  <button
                    className="af-btn af-btnGhost"
                    onClick={handleSwitchCamera}
                    disabled={saving || showPreview || camState === "requesting"}
                  >
                    Cambiar cámara
                  </button>
                ) : null}

                <button className="af-btn af-btnGhost" onClick={() => window.location.reload()} disabled={saving || showPreview}>
                  Reiniciar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showPreview ? (
        <div className="af-modalOverlay" role="dialog" aria-modal="true">
          <div className="af-modal">
            <div className="af-modalHead">
              <h3 className="af-modalTitle">¿Deseas guardar esta foto?</h3>
            </div>

            <div className="af-modalBody">
              <div className="af-preview">
                {previewUrl ? <img src={previewUrl} alt="Foto tomada" /> : null}
              </div>
            </div>

            <div className="af-modalFoot">
              <button className="af-btn af-btnGhost" onClick={onRetry} disabled={saving}>
                <RotateCcw size={18} /> Reintentar
              </button>
              <button className="af-btn af-btnPrimary" onClick={onSave} disabled={saving}>
                <Save size={18} /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
